import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { changePassword } from '../../store/slices/authSlice';
import toast from 'react-hot-toast';
import PasswordGenerator from '../PasswordGenerator/PasswordGenerator';
import { changeUserPassword } from '../../store/slices/adminSlice';

const ChangePasswordModal = ({ isOpen, onClose, mode = 'self', userId = null }) => {
  const dispatch = useAppDispatch();
  const { passwordChangeLoading } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
  const [errors, setErrors] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData(prev => ({
      ...prev,
      password: password,
      password_confirmation: password
    }));
  };

  const usePassword = () => {
    setFormData(prev => ({
      ...prev,
      password_confirmation: prev.password
    }));
  };

  const handleSave = async () => {
    const newErrors = {
      current_password: '',
      password: '',
      password_confirmation: ''
    };

    // Only validate current password for self change
    if (mode === 'self' && !formData.current_password.trim()) {
      newErrors.current_password = 'Please enter your current password.';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Please enter your new password.';
    } else if (formData.password.length < 11) {
      newErrors.password = 'New Password must include at least 11 characters consisting of lower & upper case letters, numbers and special characters.';
    }

    if (mode === 'self') {
      if (!formData.password_confirmation.trim()) {
        newErrors.password_confirmation = 'To confirm, please enter your new password again.';
      } else if (formData.password_confirmation.length < 11) {
        newErrors.password_confirmation = 'The Confirm Password must be at least 11 characters.';
      } else if (formData.password_confirmation !== formData.password) {
        newErrors.password_confirmation = 'Passwords do not match.';
      }
    }

    if (newErrors.current_password || newErrors.password || newErrors.password_confirmation) {
      setErrors(newErrors);
      return;
    }

    try {

      if (mode === 'self') {
        await dispatch(changePassword({
          current_password: formData.current_password,
          password: formData.password
        })).unwrap();
      } else {
        await dispatch(changeUserPassword({
          id: userId,
          password: formData.password
        })).unwrap();
      }

      toast.success('Password changed successfully');
      handleClose();

    } catch (error) {
      // Handle backend errors
      console.log('Password change error:', error);
      
      // Check if error has errors array (validation errors from backend)
      if (error?.errors && Array.isArray(error.errors)) {
        const backendErrors = {
          current_password: '',
          password: '',
          password_confirmation: ''
        };
        
        // Map backend errors to form fields
        error.errors.forEach(errorMsg => {
          const lowerMsg = errorMsg.toLowerCase();
          
          if (lowerMsg.includes('current password')) {
            backendErrors.current_password = errorMsg;
          } else if (lowerMsg.includes('new password') || lowerMsg.includes('password must')) {
            backendErrors.password = errorMsg;
          } else if (lowerMsg.includes('confirm')) {
            backendErrors.password_confirmation = errorMsg;
          }
        });
        
        setErrors(backendErrors);
        return;
      }
      
      // Handle single error message
      const errorMessage = error?.message || error || 'Failed to change password';
      
      // Check if it's a current password error
      if (errorMessage.toLowerCase().includes('current password') || 
          errorMessage.toLowerCase().includes('incorrect') ||
          errorMessage.toLowerCase().includes('wrong password')) {
        setErrors(prev => ({
          ...prev,
          current_password: 'Current Password incorrect.'
        }));
      } else if (errorMessage.toLowerCase().includes('new password') || 
                 errorMessage.toLowerCase().includes('password must')) {
        setErrors(prev => ({
          ...prev,
          password: errorMessage
        }));
      } else {
        // Show generic error as toast
        toast.error(errorMessage);
      }
    }
  };

  const handleClose = () => {
    setFormData({
      current_password: '',
      password: '',
      password_confirmation: ''
    });
    setShowPassword({
      current: false,
      new: false,
      confirm: false
    });
    setErrors({
      current_password: '',
      password: '',
      password_confirmation: ''
    });
    setShowPasswordGenerator(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] animate-[fadeIn_0.3s_ease-in-out] p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg p-0 w-full max-w-[700px] max-h-[90vh] overflow-y-auto relative shadow-[0_4px_20px_rgba(0,0,0,0.15)] animate-[slideDown_0.4s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header p-[15px] md:p-[20px] border-b border-[#e5e5e5] border-b-[#f4f4f4]">
          <h2 className="m-0 leading-[1.42857143] font-['Bahnschrift_Light'] text-base md:text-lg font-medium">
            Change Password
          </h2>
          <button className="close float-right text-[21px] font-bold leading-none text-black opacity-20 hover:opacity-50 p-0 cursor-pointer bg-transparent border-0 -mt-[2px]" onClick={handleClose}>×</button>
        </div>

        <div className="relative p-[15px] md:p-[20px] m-0">
          <div className="space-y-4">
            {/* Decoy inputs to prevent chrome from traversing up the DOM and autofilling the background search input */}
            <input type="text" style={{ display: 'none' }} tabIndex="-1" autoComplete="username" />
            <input type="password" style={{ display: 'none' }} tabIndex="-1" autoComplete="current-password" />

            {/* Current Password */}
            {mode === 'self' && (
              <div>
                <label className="inline-block max-w-full mb-[5px] font-bold text-[13px]">
                  Current Password <span className="text-red-600 ml-0.5">*</span>
                  <FontAwesomeIcon icon={faInfoCircle} className="ml-1.5 text-gray-600 text-sm cursor-help" />
                </label>
                <input
                  type="password"
                  name="current_password"
                  autoComplete="off"
                  style={{ textTransform: 'none' }}
                  className="password-field appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[13px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-white"
                  placeholder="Current Password"
                  value={formData.current_password}
                  onChange={handleChange}
                />
                {errors.current_password && (
                  <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.current_password}</p>
                )}
              </div>
            )}

            {/* New Password */}
            <div>
              <label className="inline-block max-w-full mb-[5px] font-bold text-[13px]">
                New Password <span className="text-red-600 ml-0.5">*</span>
                <FontAwesomeIcon icon={faInfoCircle} className="ml-1.5 text-gray-600 text-sm cursor-help" />
              </label>
              <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-0">
                <div className="relative flex-1">
                  <input
                    type={showPassword.new ? 'text' : 'password'}
                    name="password"
                    autoComplete="off"
                    style={{ textTransform: 'none' }}
                    className="password-field appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] sm:rounded-l-[5px] sm:rounded-r-none text-[#4e4c4c] font-semibold text-[13px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-white"
                    placeholder="New Password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-600 text-base p-1.5 z-10 hover:text-gray-800"
                    onClick={() => togglePasswordVisibility('new')}
                  >
                    <FontAwesomeIcon icon={showPassword.new ? faEyeSlash : faEye} />
                  </button>
                </div>
                <button
                  type="button"
                  className="relative rounded-[5px] sm:rounded-tl-none sm:rounded-bl-none shadow-none border border-transparent text-[13px] text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] shrink-0 w-full sm:w-auto sm:min-w-[160px] whitespace-nowrap px-3 py-1.5 h-[34px]"
                  onClick={generatePassword}
                >
                  Generate Password
                </button>
              </div>
              {errors.password && (
                <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.password}</p>
              )}
              <div className="text-right">
                <button
                  type="button"
                  className="bg-transparent border-none text-gray-600 text-[13px] cursor-pointer py-1.5 px-0 mt-1.5 inline-block hover:text-gray-800"
                  onClick={() => setShowPasswordGenerator(true)}
                >
                  Advance Setting
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            {mode === 'self' && (
              <div>
                <label className="inline-block max-w-full mb-[5px] font-bold text-[13px]">
                  Confirm Password <span className="text-red-600 ml-0.5">*</span>
                  <FontAwesomeIcon icon={faInfoCircle} className="ml-1.5 text-gray-600 text-sm cursor-help" />
                </label>
                <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-0">
                  <div className="relative flex-1">
                    <input
                      type={showPassword.confirm ? 'text' : 'password'}
                      name="password_confirmation"
                      autoComplete="off"
                      style={{ textTransform: 'none' }}
                      className="password-field appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] sm:rounded-l-[5px] sm:rounded-r-none text-[#4e4c4c] font-semibold text-[13px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-white"
                      placeholder="Confirm Password"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-600 text-base p-1.5 z-10 hover:text-gray-800"
                      onClick={() => togglePasswordVisibility('confirm')}
                    >
                      <FontAwesomeIcon icon={showPassword.confirm ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="relative rounded-[5px] sm:rounded-tl-none sm:rounded-bl-none shadow-none border border-transparent text-[13px] text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] shrink-0 w-full sm:w-auto sm:min-w-[160px] whitespace-nowrap px-3 py-1.5 h-[34px]"
                    onClick={usePassword}
                  >
                    Use Password
                  </button>
                </div>
                {errors.password_confirmation && (
                  <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.password_confirmation}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-[15px] md:p-[20px] text-right border-t border-[#f4f4f4] flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-2">
          <button
            className="text-black h-9 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-[#f4f4f4] text-[#444] border border-[#ddd] rounded shadow-none w-full sm:w-24"
            onClick={handleClose}
          >
            Close
          </button>
          <button
            className="text-black h-9 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded shadow-none w-full sm:w-24 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={passwordChangeLoading}
          >
            {passwordChangeLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <PasswordGenerator
        isOpen={showPasswordGenerator}
        onClose={() => setShowPasswordGenerator(false)}
      />

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ChangePasswordModal;
